import { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Command as CommandIcon, KeyRound, LogOut, Moon, Plus, Search, Settings, Sun, Monitor, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/theme-context";
import { useUnreadNotifications } from "@/hooks/queries/useDashboardData";
import { initials } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { searchableNav } from "./navigation";
import { CommandPalette } from "./CommandPalette";
import { openCommandPalette } from "./command-palette-events";

const iconBtn =
  "relative h-11 w-11 shrink-0 rounded-xl bg-card flex items-center justify-center text-foreground hover:bg-surface-muted transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

function QuickSearch({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Delayed close on blur (lets result clicks land); cancelled if focus returns first.
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return searchableNav(isAdmin)
      .filter((i) => `${i.name} ${i.keywords ?? ""}`.toLowerCase().includes(term))
      .slice(0, 7);
  }, [q, isAdmin]);

  // "/" focuses search (⌘K opens the command palette instead).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.closest("input, textarea, [contenteditable=true]");
      if (e.key === "/" && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (href: string) => {
    navigate(href);
    setQ("");
    inputRef.current?.blur();
  };

  return (
    <div className="relative w-full min-w-0 max-w-[400px]">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground pointer-events-none" strokeWidth={1.6} />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setCursor(0);
        }}
        onFocus={() => {
          clearTimeout(blurTimer.current);
          setFocused(true);
        }}
        onBlur={() => {
          clearTimeout(blurTimer.current);
          blurTimer.current = setTimeout(() => setFocused(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((c) => Math.max(0, Math.min(c + 1, results.length - 1)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
          }
          else if (e.key === "Enter" && results[cursor]) go(results[cursor].href);
          else if (e.key === "Escape") {
            setQ("");
            inputRef.current?.blur();
          }
        }}
        placeholder="Quick search"
        aria-label="Quick search"
        className="h-11 w-full rounded-xl bg-card pl-11 pr-16 text-[15px] placeholder:text-subtle focus:outline-hidden focus:ring-2 focus:ring-ring"
      />
      <button
        type="button"
        onClick={openCommandPalette}
        aria-label="Open command palette"
        title="Command palette (⌘K)"
        className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2 items-center gap-1 rounded-lg border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-surface-muted"
      >
        <CommandIcon className="h-3 w-3" />K
      </button>
      {focused && q.trim() && results.length === 0 && (
        <p className="absolute z-50 mt-2 w-full rounded-xl bg-popover px-4 py-3 text-sm text-muted-foreground shadow-[0_8px_32px_rgb(0_0_0/0.08)] border border-border/60">
          No matching pages
        </p>
      )}
      {focused && results.length > 0 && (
        <ul role="listbox" aria-label="Search results" className="absolute z-50 mt-2 w-full rounded-xl bg-popover p-1.5 shadow-[0_8px_32px_rgb(0_0_0/0.08)] border border-border/60">
          {results.map((r, i) => (
            <li key={r.href} role="option" aria-selected={i === cursor}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(r.href)}
                onMouseEnter={() => setCursor(i)}
                className={cn(
                  "w-full text-left flex items-center justify-between px-3 h-10 rounded-lg text-sm",
                  i === cursor ? "bg-surface-muted" : ""
                )}
              >
                <span>{r.name}</span>
                <span className="text-xs text-subtle">{r.href}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TopBar({ leading }: { leading?: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { data: unread = 0 } = useUnreadNotifications();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const name = user?.username || "User";

  return (
    <header className="flex items-center gap-3 h-[72px]">
      {leading}
      <QuickSearch isAdmin={isAdmin} />
      <CommandPalette />
      <div className="ml-auto flex items-center gap-2.5">
        <button type="button" className={cn(iconBtn, "sm:hidden")} aria-label="Open command palette" onClick={openCommandPalette}>
          <CommandIcon className="h-[18px] w-[18px]" strokeWidth={1.6} />
        </button>
        <button className={iconBtn} aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`} onClick={() => navigate("/notifications")}>
          <Bell className="h-[19px] w-[19px]" strokeWidth={1.6} />
          {unread > 0 && (
            <span className="absolute top-2 right-2 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={iconBtn} aria-label="Appearance">
              <Sun className="h-[19px] w-[19px] dark:hidden" strokeWidth={1.6} />
              <Moon className="h-[19px] w-[19px] hidden dark:block" strokeWidth={1.6} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl">
            {([
              ["light", Sun, "Light"],
              ["dark", Moon, "Dark"],
              ["system", Monitor, "System"],
            ] as const).map(([v, Icon, label]) => (
              <DropdownMenuItem key={v} onClick={() => setTheme(v)} className={cn("rounded-lg", theme === v && "bg-surface-muted")}>
                <Icon className="mr-2 h-4 w-4" /> {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Account menu"
              className="flex h-11 shrink-0 items-center gap-2.5 rounded-xl bg-card px-1.5 md:pr-3 hover:bg-surface-muted transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="h-8 w-8 rounded-[10px] bg-brand text-brand-foreground text-xs font-semibold flex items-center justify-center">
                {initials(name)}
              </span>
              <span className="hidden md:flex flex-col items-start leading-tight max-w-[160px]">
                <span className="text-[13px] font-medium truncate max-w-full">{name}</span>
                <span className="text-[11px] text-muted-foreground truncate max-w-full">{user?.email}</span>
              </span>
              <ChevronDown className="hidden md:block h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-xl">
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-medium">{name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="rounded-lg">
              <Link to="/settings">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="rounded-lg">
              <Link to="/api-keys">
                <KeyRound className="mr-2 h-4 w-4" /> API keys
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="rounded-lg">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Link
          to="/playground"
          className="hidden lg:flex h-11 items-center gap-2 rounded-xl bg-card px-4 text-[15px] font-medium hover:bg-surface-muted transition-colors"
        >
          <Plus className="h-[18px] w-[18px]" strokeWidth={1.8} /> New request
        </Link>
      </div>
    </header>
  );
}
